import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { NoAccess } from './NoAccess';

/**
 * Routes the bare "/" path to the right landing page for the current
 * effective role. Entrants go to their entry list; staff roles go to
 * the admin dashboard.
 *
 * Holds rendering until the auth context has resolved — otherwise
 * a render that fires before login state propagates would see
 * `hasRole('Entrant') === false` and navigate the entrant to the admin
 * dashboard, which then sticks because the navigation already happened.
 */
export function RoleHome() {
  const { hasRole, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (hasRole('Entrant')) return <Navigate to="/entrant" replace />;
  if (hasRole('Admin') || hasRole('User')) return <Navigate to="/dashboard" replace />;
  // Read-only PR role has no dashboard — land it straight on the entries list.
  if (hasRole('PR')) return <Navigate to="/entries" replace />;
  // Authenticated but holding no role the portal recognises — show a dead-end
  // screen (+ telemetry) instead of falling through to /dashboard, whose role
  // guard would bounce them back here in an invisible loop.
  return <NoAccess />;
}
